import { z } from 'zod';
import { LegalStatusValueSchema, SourceVerificationSchema } from './legal';
import { EditorialStatusSchema } from './workspace-content';

/**
 * Editorial review of the legal corpus (M3, ADR-023). People record each verification
 * dimension separately; model output never changes a verdict. A review's current state lives
 * in one record, and every decision is also kept, unchanged, in its history.
 */

export const ReviewCollectionSchema = z.enum(['acts', 'cases']);

export const SourceReviewSchema = z.object({
  versionId: z.string(),
  sourceId: z.string(),
  collection: ReviewCollectionSchema,
  title: z.string(),
  state: EditorialStatusSchema,
  verification: SourceVerificationSchema,
  /** Increases with every decision; a decision names the revision it was made against. */
  revision: z.number().int().nonnegative(),
  updatedAt: z.string().nullable(),
});
export type SourceReview = z.infer<typeof SourceReviewSchema>;

export const EditorialRoleSchema = z.enum(['legal_editor', 'legal_reviewer']);

export const ReviewEventSchema = z.object({
  id: z.string(),
  versionId: z.string(),
  at: z.string(),
  actorUid: z.string(),
  actorName: z.string().nullable(),
  actorRole: EditorialRoleSchema,
  kind: z.enum(['dimension', 'state']),
  dimension: z
    .enum(['authenticity', 'text_integrity', 'legal_status', 'legal_analysis'])
    .optional(),
  verdict: z.string().optional(),
  status: LegalStatusValueSchema.optional(),
  asOf: z.string().optional(),
  fromState: EditorialStatusSchema.optional(),
  toState: EditorialStatusSchema.optional(),
  note: z.string(),
});
export type ReviewEvent = z.infer<typeof ReviewEventSchema>;

export const ReviewListSchema = z.object({ reviews: z.array(SourceReviewSchema) });
export type ReviewList = z.infer<typeof ReviewListSchema>;

export const ReviewDetailSchema = z.object({
  review: SourceReviewSchema,
  events: z.array(ReviewEventSchema),
  /** What the caller may decide: editors the text dimensions, reviewers everything. */
  canReview: z.boolean(),
  canApprove: z.boolean(),
});
export type ReviewDetail = z.infer<typeof ReviewDetailSchema>;

/** The evidence or reason for a decision: always required, so the history explains itself. */
const Note = z.string().trim().min(3).max(2000);
const Revision = z.number().int().nonnegative();
const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const ReviewDecisionSchema = z.union([
  z
    .object({
      kind: z.literal('dimension'),
      dimension: z.enum(['authenticity', 'text_integrity']),
      verdict: z.enum(['pending', 'reviewed', 'rejected']),
      note: Note,
      expectedRevision: Revision,
    })
    .strict(),
  z
    .object({
      kind: z.literal('dimension'),
      dimension: z.literal('legal_status'),
      verdict: z.enum(['pending', 'reviewed', 'unknown']),
      status: LegalStatusValueSchema.optional(),
      asOf: IsoDate.optional(),
      note: Note,
      expectedRevision: Revision,
    })
    .strict()
    // A reviewed status means nothing without what it is and the date it holds as of.
    .refine((d) => d.verdict !== 'reviewed' || (d.status !== undefined && d.asOf !== undefined), {
      message: 'A reviewed legal status needs the status and the date it is assessed as of',
    }),
  z
    .object({
      kind: z.literal('dimension'),
      dimension: z.literal('legal_analysis'),
      verdict: z.enum(['pending', 'reviewed', 'disputed', 'not_applicable']),
      note: Note,
      expectedRevision: Revision,
    })
    .strict(),
  z
    .object({
      kind: z.literal('state'),
      state: z.enum([
        'under_review',
        'approved_for_defined_use',
        'provisional',
        'rejected',
        'needs_more_evidence',
      ]),
      note: Note,
      expectedRevision: Revision,
    })
    .strict(),
]);
export type ReviewDecision = z.infer<typeof ReviewDecisionSchema>;

/** What readers see of a review: verdicts and dates, never notes or reviewer identities. */
export const PublicReviewSchema = z.object({
  versionId: z.string(),
  state: EditorialStatusSchema,
  verification: SourceVerificationSchema,
});
export type PublicReview = z.infer<typeof PublicReviewSchema>;
export const PublicReviewListSchema = z.object({ reviews: z.array(PublicReviewSchema) });
export type PublicReviewList = z.infer<typeof PublicReviewListSchema>;
